import { useState, useRef, useEffect } from "react";
import { JsonViewer } from "@textea/json-viewer";
import { DayPicker } from "react-day-picker";
import "react-day-picker/src/style.css";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { LoadingThrobber } from "../base_components/loading_throbber";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { SeshWarningButton } from "../base_components/sesh_warning_button";
import { SeshSelect } from "../base_components/sesh_select_input";
import { SeshTextInput } from "../base_components/sesh_text_input";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import ClipLoader from "react-spinners/ClipLoader";
import {
	getTiersWithoutPrices,
	StripeProductId,
} from "../../../../../../common/premium_tiers";

export function AdminStripeManager() {
	const context = trpc.useContext();

	const active_coupon_query = trpc.admin.getActiveCoupon.useQuery();

	const active_coupon_mutation = trpc.admin.setActiveCoupon.useMutation({
		onSuccess: () => {
			context.admin.getActiveCoupon.invalidate();
		},
	});

	const setActiveCoupon = (coupon_id: string | null) =>
		active_coupon_mutation.mutate({ coupon_id });

	if (!active_coupon_query.isSuccess) {
		return <LoadingThrobber />;
	}

	return (
		<div className="flex flex-col gap-10">
			<CheckoutEnabler />
			<PriceCacheClearer />
			<div className="flex flex-col gap-2">
				<div className="flex items-center">
					<div className="font-bold">Current Active Coupon</div>
					<SeshWarningButton
						className="ml-auto !w-fit"
						onClick={() => setActiveCoupon(null)}
						is_loading={active_coupon_mutation.isLoading}
					>
						Clear Active Coupon
					</SeshWarningButton>
				</div>
				{active_coupon_query.data ? (
					<div className="flex flex-col gap-2">
						<div className="rounded bg-base-light p-3">
							<div className="mb-2 text-sm text-slate-400">
								Active Coupon Details:
							</div>
							<div className="grid grid-cols-2 gap-2 text-sm">
								<div>
									<span className="text-slate-400">Name:</span>{" "}
									{active_coupon_query.data.api_data.name}
								</div>
								<div>
									<span className="text-slate-400">Discount:</span>{" "}
									{active_coupon_query.data.api_data.percent_off
										? `${active_coupon_query.data.api_data.percent_off}%`
										: `$${(active_coupon_query.data.api_data.amount_off / 100).toFixed(2)}`}
								</div>
								<div>
									<span className="text-slate-400">End Date:</span>{" "}
									{active_coupon_query.data.end_date
										? new Date(active_coupon_query.data.end_date).toLocaleString()
										: "No expiry"}
								</div>
								<div>
									<span className="text-slate-400">Applies To:</span>{" "}
									{active_coupon_query.data.applies_to_products?.length
										? `${active_coupon_query.data.applies_to_products.length} product(s)`
										: "All products"}
								</div>
							</div>
						</div>
						<JsonViewer value={active_coupon_query.data} theme="dark" defaultInspectDepth={1} />
					</div>
				) : (
					<>None</>
				)}
			</div>
			<CouponCreator />
			<CouponList
				active_coupon_id={active_coupon_query.data?.id}
				setActiveCoupon={setActiveCoupon}
				is_loading={active_coupon_mutation.isLoading}
			/>
		</div>
	);
}

function PriceCacheClearer() {
	const mutation = trpc.admin.clearStripePriceCache.useMutation();

	return (
		<div className="flex items-center">
			<div className="font-bold">Price Cache</div>
			<SeshWarningButton
				className="ml-auto !w-fit"
				onClick={() => mutation.mutate()}
				is_loading={mutation.isLoading}
			>
				Clear Price Cache
			</SeshWarningButton>
		</div>
	);
}

function CheckoutEnabler(props: {}) {
	const context = trpc.useContext();

	const checkout_disabled_query = trpc.admin.getCheckoutDisabled.useQuery();

	const mutation = trpc.admin.modifyCheckoutDisabled.useMutation({
		onSuccess: () => {
			context.admin.getCheckoutDisabled.invalidate();
		},
	});

	if (checkout_disabled_query.isLoading) {
		return <ClipLoader />;
	}

	return (
		<div>
			<div className="font-bold">Checkout Status</div>
			<div className="flex items-center gap-2">
				Current Setting:{" "}
				{checkout_disabled_query.data ? "Disabled" : "Enabled"}
				{checkout_disabled_query.data ? (
					<SeshPrimaryButton
						className="ml-auto !w-fit"
						is_loading={mutation.isLoading}
						onClick={() =>
							mutation.mutate({
								is_disabled: !checkout_disabled_query.data,
							})
						}
					>
						{checkout_disabled_query.data ? "Enable" : "Disable"}
					</SeshPrimaryButton>
				) : (
					<SeshWarningButton
						className="ml-auto !w-fit"
						is_loading={mutation.isLoading}
						onClick={() =>
							mutation.mutate({
								is_disabled: !checkout_disabled_query.data,
							})
						}
					>
						{checkout_disabled_query.data ? "Enable" : "Disable"}
					</SeshWarningButton>
				)}
			</div>
		</div>
	);
}

function CouponCreator() {
	const [showModal, setShowModal] = useState(false);

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center">
				<div className="font-bold">Create New Coupon</div>
				<SeshPrimaryButton
					className="ml-auto !w-fit"
					onClick={() => setShowModal(true)}
				>
					Create Coupon
				</SeshPrimaryButton>
			</div>
			<CreateCouponModal
				is_open={showModal}
				onClose={() => setShowModal(false)}
			/>
		</div>
	);
}

type DiscountType = "percent_off" | "amount_off";

function CreateCouponModal(props: { is_open: boolean; onClose: () => void }) {
	const context = trpc.useContext();
	const tiers = getTiersWithoutPrices();

	const [name, setName] = useState("");
	const [discountType, setDiscountType] = useState<DiscountType>("percent_off");
	const [discountValue, setDiscountValue] = useState("");
	const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
	const [endDate, setEndDate] = useState<Date | null>(null);
	const [error, setError] = useState<string | null>(null);

	const createMutation = trpc.admin.createCoupon.useMutation({
		onSuccess: () => {
			context.admin.getCoupons.invalidate();
			resetForm();
			props.onClose();
		},
		onError: (err) => {
			setError(err.message);
		},
	});

	const resetForm = () => {
		setName("");
		setDiscountType("percent_off");
		setDiscountValue("");
		setSelectedProducts([]);
		setEndDate(null);
		setError(null);
	};

	const handleClose = () => {
		resetForm();
		props.onClose();
	};

	const handleSubmit = () => {
		setError(null);

		if (!name.trim()) {
			setError("Name is required");
			return;
		}

		const value = parseInt(discountValue, 10);
		if (isNaN(value) || value <= 0) {
			setError("Discount value must be a positive number");
			return;
		}

		if (discountType === "percent_off" && (value < 1 || value > 100)) {
			setError("Percentage must be between 1 and 100");
			return;
		}

		if (selectedProducts.length === 0) {
			setError("Select at least one product");
			return;
		}

		if (!endDate) {
			setError("End date is required");
			return;
		}

		createMutation.mutate({
			name: name.trim(),
			discount_type: discountType,
			discount_value: value,
			product_ids: selectedProducts,
			end_date: endDate.toISOString(),
		});
	};

	const toggleProduct = (productId: string) => {
		setSelectedProducts((prev) =>
			prev.includes(productId)
				? prev.filter((id) => id !== productId)
				: [...prev, productId]
		);
	};

	// Get unique products (filter out duplicates like sesh_pro vs prod_*)
	const productOptions = Object.entries(tiers)
		.filter(([id, tier]) => {
			// Skip the generic sesh_pro identifier, keep actual product IDs
			if (id === "sesh_pro") return false;
			return true;
		})
		.map(([id, tier]) => ({
			id,
			title: tier.title,
		}));

	return (
		<DashboardUtils.SeshModal
			is_active={props.is_open}
			onClose={handleClose}
			title="Create New Coupon"
			allow_overflow={true}
			content={
				<div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto p-4">
					<SeshTextInput
						label="Coupon Name"
						help_text="This will be publicly visible to users"
						input_props={{
							type: "text",
							value: name,
							onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
								setName(e.target.value),
							placeholder: "e.g., Black Friday Sale",
						}}
					/>

					<div className="flex flex-col gap-2">
						<label className="label">Discount Type</label>
						<div className="flex gap-4">
							<label className="flex cursor-pointer items-center gap-2">
								<input
									type="radio"
									name="discountType"
									checked={discountType === "percent_off"}
									onChange={() => setDiscountType("percent_off")}
									className="h-4 w-4"
								/>
								<span>Percentage Off</span>
							</label>
							<label className="flex cursor-pointer items-center gap-2">
								<input
									type="radio"
									name="discountType"
									checked={discountType === "amount_off"}
									onChange={() => setDiscountType("amount_off")}
									className="h-4 w-4"
								/>
								<span>Fixed Amount Off (USD)</span>
							</label>
						</div>
					</div>

					<SeshTextInput
						label={
							discountType === "percent_off"
								? "Percentage Discount"
								: "Amount Off (in cents)"
						}
						help_text={
							discountType === "percent_off"
								? "Enter a value between 1 and 100"
								: "Enter amount in cents (e.g., 500 = $5.00)"
						}
						input_props={{
							type: "number",
							value: discountValue,
							onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
								setDiscountValue(e.target.value),
							placeholder: discountType === "percent_off" ? "20" : "500",
							min: 1,
							max: discountType === "percent_off" ? 100 : undefined,
						}}
					/>

					<div className="flex flex-col gap-2">
						<label className="label">Apply to Products</label>
						<p className="help mb-1">
							Select which products this coupon applies to
						</p>
						<div className="flex flex-col gap-2 rounded bg-base-light p-3">
							{productOptions.map(({ id, title }) => (
								<label
									key={id}
									className="flex cursor-pointer items-center gap-2"
								>
									<input
										type="checkbox"
										checked={selectedProducts.includes(id)}
										onChange={() => toggleProduct(id)}
										className="h-4 w-4"
									/>
									<span>{title}</span>
									<span className="text-xs text-slate-400">({id})</span>
								</label>
							))}
						</div>
					</div>

					<div className="flex flex-col gap-2">
						<label className="label">End Date</label>
						<p className="help mb-1">
							When the sale ends (users cannot redeem after this date)
						</p>
						<DatePickerInput
							value={endDate}
							onChange={setEndDate}
							placeholder="Select end date..."
						/>
					</div>

					<div className="rounded bg-slate-700/50 p-3 text-sm">
						<div className="flex items-center gap-2">
							<i className="fas fa-info-circle text-sesh-primary"></i>
							<span className="font-semibold">Duration: Forever</span>
						</div>
						<p className="mt-1 text-slate-400">
							Once applied, this coupon will persist for the full lifetime of
							a user's subscription. The end date only controls when new
							users can redeem the coupon.
						</p>
					</div>

					{error && (
						<div className="rounded bg-red-900/30 p-3 text-sm text-red-400">
							{error}
						</div>
					)}

					<div className="flex justify-end gap-2">
						<SeshWarningButton
							onClick={handleClose}
							className="!w-fit"
							disabled={createMutation.isLoading}
						>
							Cancel
						</SeshWarningButton>
						<SeshPrimaryButton
							onClick={handleSubmit}
							className="!w-fit"
							is_loading={createMutation.isLoading}
						>
							Create Coupon
						</SeshPrimaryButton>
					</div>
				</div>
			}
		/>
	);
}

function CouponList(props: {
	active_coupon_id: string | undefined;
	setActiveCoupon: (coupon_id: string | null) => void;
	is_loading?: boolean;
}) {
	const context = trpc.useContext();
	const all_coupons_query = trpc.admin.getCoupons.useQuery();
	const [expandedCoupon, setExpandedCoupon] = useState<string | null>(null);

	const removeMutation = trpc.admin.removeManagedCoupon.useMutation({
		onSuccess: () => {
			context.admin.getCoupons.invalidate();
			context.admin.getAllStripeCoupons.invalidate();
		},
	});

	if (!all_coupons_query.isSuccess) {
		return <LoadingThrobber />;
	}

	const coupons = all_coupons_query.data;

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-2">
				<div className="font-bold">Managed Coupons ({coupons.length})</div>
				<p className="text-sm text-slate-400">
					These coupons are managed through this admin UI. Use "Import" below to add existing Stripe coupons.
				</p>
				<div className="flex flex-col gap-2">
					{coupons.length === 0 ? (
						<div className="rounded bg-base-light p-3 text-slate-400">
							No managed coupons. Create a new coupon or import an existing one from Stripe.
						</div>
					) : (
						coupons.map((coupon) => {
							const isActive = props.active_coupon_id === coupon.id;
							const isExpired = coupon.redeem_by
								? coupon.redeem_by * 1000 < Date.now()
								: false;
							const isExpanded = expandedCoupon === coupon.id;

							return (
								<div
									key={coupon.id}
									className={`rounded border p-3 ${
										isActive
											? "border-sesh-primary bg-sesh-primary/10"
											: "border-slate-600 bg-base-light"
									}`}
								>
									<div className="flex items-center gap-3">
										<div className="flex flex-grow flex-col">
											<div className="flex items-center gap-2">
												<span className="font-semibold">
													{coupon.name || coupon.id}
												</span>
												{isActive && (
													<span className="rounded bg-sesh-primary px-2 py-0.5 text-xs font-bold text-base-dark">
														ACTIVE
													</span>
												)}
												{isExpired && (
													<span className="rounded bg-red-600 px-2 py-0.5 text-xs font-bold">
														EXPIRED
													</span>
												)}
											</div>
											<div className="mt-1 flex flex-wrap gap-4 text-sm text-slate-400">
												<span>
													{coupon.percent_off
														? `${coupon.percent_off}% off`
														: `$${(coupon.amount_off / 100).toFixed(2)} off`}
												</span>
												{coupon.redeem_by && (
													<span>
														Ends:{" "}
														{new Date(
															coupon.redeem_by * 1000
														).toLocaleDateString()}
													</span>
												)}
												{coupon.applies_to?.products && (
													<span>
														{coupon.applies_to.products.length} product(s)
													</span>
												)}
											</div>
										</div>
										<div className="flex gap-2">
											<button
												className="text-slate-400 hover:text-white"
												onClick={() =>
													setExpandedCoupon(isExpanded ? null : coupon.id)
												}
											>
												<i
													className={`fas fa-chevron-${
														isExpanded ? "up" : "down"
													}`}
												></i>
											</button>
											{!isActive && (
												<SeshWarningButton
													className="!w-fit text-sm"
													onClick={() =>
														removeMutation.mutate({ coupon_id: coupon.id })
													}
													is_loading={removeMutation.isLoading}
												>
													Remove
												</SeshWarningButton>
											)}
											{!isActive && !isExpired && (
												<SeshPrimaryButton
													className="!w-fit text-sm"
													onClick={() => props.setActiveCoupon(coupon.id)}
													is_loading={props.is_loading}
												>
													Set Active
												</SeshPrimaryButton>
											)}
										</div>
									</div>
									{isExpanded && (
										<div className="mt-3 border-t border-slate-600 pt-3">
											<JsonViewer
												value={coupon}
												theme="dark"
												defaultInspectDepth={2}
											/>
										</div>
									)}
								</div>
							);
						})
					)}
				</div>
			</div>

			{/* Import section */}
			<CouponImporter />
		</div>
	);
}

function CouponImporter() {
	const context = trpc.useContext();
	const allStripeCouponsQuery = trpc.admin.getAllStripeCoupons.useQuery();
	const [selectedCouponId, setSelectedCouponId] = useState<string | null>(null);

	const importMutation = trpc.admin.importCoupon.useMutation({
		onSuccess: () => {
			context.admin.getCoupons.invalidate();
			context.admin.getAllStripeCoupons.invalidate();
			setSelectedCouponId(null);
		},
	});

	if (!allStripeCouponsQuery.isSuccess) {
		return <LoadingThrobber />;
	}

	// Filter to only show unmanaged coupons
	const unmanagedCoupons = allStripeCouponsQuery.data.filter(
		(coupon) => !coupon.is_managed
	);

	if (unmanagedCoupons.length === 0) {
		return (
			<div className="flex flex-col gap-2">
				<div className="font-bold">Import from Stripe</div>
				<div className="rounded bg-base-light p-3 text-sm text-slate-400">
					All Stripe coupons are already imported.
				</div>
			</div>
		);
	}

	const selectedCoupon = unmanagedCoupons.find((c) => c.id === selectedCouponId);

	return (
		<div className="flex flex-col gap-2">
			<div className="font-bold">Import from Stripe</div>
			<p className="text-sm text-slate-400">
				Import existing Stripe coupons to manage them here.
			</p>
			<div className="flex items-center gap-2">
				<div className="min-w-0 flex-grow">
					<SeshSelect
						options={unmanagedCoupons.map((coupon) => ({
							label: `${coupon.name || coupon.id} (${
								coupon.percent_off
									? `${coupon.percent_off}% off`
									: `$${(coupon.amount_off / 100).toFixed(2)} off`
							})`,
							value: coupon.id,
						}))}
						value={
							selectedCouponId
								? {
										label:
											selectedCoupon?.name ||
											selectedCouponId,
										value: selectedCouponId,
								  }
								: null
						}
						onChange={(val) => setSelectedCouponId(val?.value ?? null)}
					/>
				</div>
				<SeshPrimaryButton
					className="!w-fit"
					onClick={() => {
						if (selectedCouponId) {
							importMutation.mutate({ coupon_id: selectedCouponId });
						}
					}}
					is_loading={importMutation.isLoading}
					disabled={!selectedCouponId}
				>
					Import
				</SeshPrimaryButton>
			</div>
			{selectedCoupon && (
				<div className="mt-2 rounded bg-base-light p-3">
					<div className="text-sm text-slate-400">Preview:</div>
					<JsonViewer value={selectedCoupon} theme="dark" defaultInspectDepth={1} />
				</div>
			)}
		</div>
	);
}

// Legacy component kept for backward compatibility
function CouponSetter(props: {
	active_coupon_id: string;
	setActiveCoupon: (coupon_id: string | null) => void;
	is_loading?: boolean;
}) {
	const all_coupons_query = trpc.admin.getCoupons.useQuery(undefined, {
		onSuccess: (data) => setOption(data[0]),
	});

	const all_coupons_query_data =
		all_coupons_query.data as RouterInferenceHelpers["admin"]["getCoupons"]["output"];

	const [option, setOption] = useState<
		RouterInferenceHelpers["admin"]["getCoupons"]["output"][number]
	>(all_coupons_query_data?.[0]);

	if (!all_coupons_query.isSuccess) {
		return <DashboardUtils.RenderLoadingSelect />;
	}

	return (
		<div className="flex flex-col gap-2">
			<div className="font-bold">Set Coupon</div>
			<div className="flex gap-2">
				<div className="w-full">
					<SeshSelect
						options={all_coupons_query_data.map((val) => ({
							label:
								val.name +
								(props.active_coupon_id === val.id
									? " (active)"
									: "") +
								` (${val.id})`,
							value: val.id,
						}))}
						value={{ label: option.name, value: option.id }}
						onChange={(select_val) =>
							setOption(
								all_coupons_query_data.find(
									(val) => val.id === select_val.value
								)
							)
						}
					/>
				</div>
				<div className="ml-auto flex gap-2">
					<SeshPrimaryButton
						className="!w-fit"
						onClick={() => props.setActiveCoupon(option.id)}
						is_loading={props.is_loading}
					>
						Set
					</SeshPrimaryButton>
				</div>
			</div>
			<JsonViewer value={option} theme="dark" />
		</div>
	);
}

// Date picker component for coupon end date
function DatePickerInput(props: {
	value: Date | null;
	onChange: (date: Date | null) => void;
	placeholder?: string;
}) {
	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		}

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
			return () => {
				document.removeEventListener("mousedown", handleClickOutside);
			};
		}
	}, [isOpen]);

	const handleSelect = (day: Date | undefined) => {
		if (day) {
			// Set time to end of day (23:59:59)
			day.setHours(23, 59, 59, 999);
			props.onChange(day);
			setIsOpen(false);
		}
	};

	const formatDate = (date: Date | null) => {
		if (!date) return "";
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	return (
		<div ref={wrapperRef} className="relative">
			<input
				className="input w-full cursor-pointer rounded border border-slate-600 bg-base-light px-3 py-2 text-white"
				type="text"
				value={formatDate(props.value)}
				onClick={() => setIsOpen(!isOpen)}
				readOnly
				placeholder={props.placeholder}
			/>
			{isOpen && (
				<div className="absolute bottom-full z-50 mb-1 rounded border border-slate-600 bg-base-dark p-2 shadow-lg">
					<DayPicker
						mode="single"
						selected={props.value ?? undefined}
						onSelect={handleSelect}
						disabled={(date) => date < new Date()}
						className="date-picker-dark"
					/>
				</div>
			)}
			<style>{`
				.date-picker-dark {
					--rdp-accent-color: #00f4ff;
					--rdp-background-color: #313a46;
					color: white;
				}
				.date-picker-dark .rdp-day_selected {
					background-color: #00f4ff;
					color: #1f242b;
				}
				.date-picker-dark .rdp-day:hover:not(.rdp-day_selected) {
					background-color: #313a46;
				}
				.date-picker-dark .rdp-button:hover:not([disabled]) {
					background-color: #313a46;
				}
				.date-picker-dark .rdp-nav_button:hover {
					background-color: #313a46;
				}
				.date-picker-dark .rdp-day_disabled {
					opacity: 0.3;
				}
			`}</style>
		</div>
	);
}
